'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import {
  Checkbox,
  computeGroupCheckboxState,
  GROUP_CHECKBOX_STATE,
  isArchivedLifecycle,
  isFindingsGroupHeaderRow,
  type FindingsGridRow,
  type GroupCheckboxState,
} from '@terra/ui-design-system';
import { useFindingsTableContext } from './FindingsTableContext';
import { rowClickBlockerProps } from '../rowClickBlocker';

const toCheckboxChecked = (
  state: GroupCheckboxState,
): boolean | typeof GROUP_CHECKBOX_STATE.INDETERMINATE =>
  state === GROUP_CHECKBOX_STATE.CHECKED
    ? true
    : state === GROUP_CHECKBOX_STATE.INDETERMINATE
      ? GROUP_CHECKBOX_STATE.INDETERMINATE
      : false;

function SelectionCheckboxFrame({
  children,
  isRevealedOnHover = false,
}: {
  children: ReactNode;
  isRevealedOnHover?: boolean;
}) {
  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      {...(isRevealedOnHover
        ? {
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease',
            css: { '.ag-row-hover &': { opacity: 1, pointerEvents: 'auto' } },
          }
        : {})}
      {...rowClickBlockerProps}
    >
      {children}
    </Box>
  );
}

export function FindingsTableSelectAllHeader() {
  const t = useTranslations('proxy.findings.table');
  const {
    headerCheckboxState,
    hasLoadedActionableFindings,
    onSelectAllLoaded,
  } = useFindingsTableContext();

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectAllLoaded')}
        checked={toCheckboxChecked(headerCheckboxState)}
        disabled={!hasLoadedActionableFindings}
        onCheckedChange={(details) =>
          onSelectAllLoaded(details.checked === true)
        }
      />
    </SelectionCheckboxFrame>
  );
}

export function FindingsTableSelectionCell({
  row,
}: {
  row: FindingsGridRow | undefined;
}) {
  const t = useTranslations('proxy.findings.table');
  const { isRowSelected, onSetFindingIdsSelected } = useFindingsTableContext();

  if (!row) {
    return null;
  }

  if (isFindingsGroupHeaderRow(row)) {
    const groupState = computeGroupCheckboxState({
      itemIds: row.bulkActionableFindingIds,
      isItemSelected: isRowSelected,
    });

    return (
      <SelectionCheckboxFrame
        isRevealedOnHover={groupState === GROUP_CHECKBOX_STATE.UNCHECKED}
      >
        <Checkbox
          size="sm"
          aria-label={t('selectGroup')}
          checked={toCheckboxChecked(groupState)}
          disabled={row.bulkActionableFindingIds.length === 0}
          onCheckedChange={(details) =>
            onSetFindingIdsSelected({
              ids: row.bulkActionableFindingIds,
              selected: details.checked === true,
            })
          }
        />
      </SelectionCheckboxFrame>
    );
  }

  return (
    <SelectionCheckboxFrame>
      <Checkbox
        size="sm"
        aria-label={t('selectFinding')}
        checked={isRowSelected(row.id)}
        disabled={isArchivedLifecycle(row.lifecycle)}
        onCheckedChange={(details) =>
          onSetFindingIdsSelected({
            ids: [row.id],
            selected: details.checked === true,
          })
        }
      />
    </SelectionCheckboxFrame>
  );
}
